/**
 * savedTablesStore — persists user-saved tables from chat answers.
 *
 * Uses Zustand + localStorage (same pattern as authStore).
 * Tables survive browser refresh but are browser-local (single user/device).
 *
 * Each saved table:
 *   id          string   — random UUID
 *   name        string   — user-editable label
 *   columns     string[] — ordered column names
 *   rows        object[] — data rows keyed by column name
 *   sourceQuery string   — the chat question that produced it
 *   sessionId   string   — which chat session it came from (optional)
 *   savedAt     string   — ISO timestamp
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

function uuid() {
  return (
    Math.random().toString(36).slice(2, 9) +
    "-" +
    Date.now().toString(36)
  );
}

export const useSavedTablesStore = create(
  persist(
    (set, get) => ({
      tables: [],

      /** Save a new table. Returns the generated id. */
      saveTable: ({ name, columns, rows, sourceQuery, sessionId = "" }) => {
        const entry = {
          id: uuid(),
          name: name || sourceQuery || "Saved table",
          columns,
          rows,
          sourceQuery,
          sessionId,
          savedAt: new Date().toISOString(),
        };
        set((state) => ({ tables: [entry, ...state.tables] }));
        return entry.id;
      },

      /** Delete a saved table by id. */
      deleteTable: (id) =>
        set((state) => ({
          tables: state.tables.filter((t) => t.id !== id),
        })),

      /** Rename a saved table. */
      renameTable: (id, newName) =>
        set((state) => ({
          tables: state.tables.map((t) =>
            t.id === id ? { ...t, name: newName } : t,
          ),
        })),

      /** Look up a single table by id. */
      getTable: (id) => get().tables.find((t) => t.id === id) || null,
    }),
    {
      name: "sadt-saved-tables",
      partialize: (state) => ({ tables: state.tables }),
    },
  ),
);
